import { useEffect, useMemo } from "react";
import useDebounce from "../../Components/SearchDebonce/useDebounce";
import { useCheckDuplicatePhoneQuery } from "../../Api/attoDeskApi";
import { normalizePhone } from "./fieldValidation";

export interface DuplicateCustomer {
  customer_id: number;
  customer_name: string;
}

export type PhoneIconType = "none" | "spinner" | "error" | "success";

/**
 * Single source of truth for the phone field's validation state. Every
 * consumer — the TextField `error`, `helperText`, end-adornment icon, the
 * Save button, and the duplicate profile link — reads from this one object,
 * so phone validity is never recalculated in more than one place.
 */
export interface PhoneState {
  /** Canonical digits: non-digits stripped + leading "1" dropped from 11-digit. */
  normalizedDigits: string;
  isEmpty: boolean;
  /** 1–9 digits — still being typed; not an error. */
  isIncomplete: boolean;
  /** More than 10 digits after normalization — invalid. */
  isFormatInvalid: boolean;
  /** Exactly 10 digits — the only length the duplicate API runs for. */
  shouldCheckDuplicate: boolean;
  /** Duplicate lookup is in flight or about to fire. */
  isChecking: boolean;
  isDuplicate: boolean;
  duplicateCustomer: DuplicateCustomer | null;
  /** Server has confirmed an exactly-10-digit number is unique. */
  isVerifiedUnique: boolean;
  /** Message to surface under the field, if any. */
  errorMessage?: string;
  /** Which end-adornment icon to render. */
  iconType: PhoneIconType;
}

/**
 * Real-time duplicate-phone detection for the customer create/edit forms.
 *
 * - Length facts (empty / incomplete / format-invalid) are derived from the
 *   LIVE value so they update instantly while typing — no debounce lag.
 * - The duplicate API runs ONLY at exactly 10 normalized digits, keyed off the
 *   DEBOUNCED value (~500ms).
 * - An API result is trusted only while it matches the current live value
 *   (`inSync`), so a stale "unique" result can never linger as a green tick.
 * - `excludeId` is passed in edit mode so a customer never flags itself.
 */
export function useDuplicatePhoneCheck(
  phone: string,
  excludeId?: number | string
): PhoneState {
  const debounced = useDebounce(phone || "", 500);

  const liveDigits = normalizePhone(phone || "");
  const debouncedDigits = normalizePhone(debounced || "");
  const inSync = liveDigits === debouncedDigits;
  const ready = debouncedDigits.length === 10; // only ever query at exactly 10

  const { data, isFetching, error } = useCheckDuplicatePhoneQuery(
    { phone: debounced, exclude_id: excludeId },
    { skip: !ready }
  );

  const phoneState = useMemo<PhoneState>(() => {
    const len = liveDigits.length;
    const base = {
      normalizedDigits: liveDigits,
      isEmpty: len === 0,
      isIncomplete: len >= 1 && len <= 9,
      isFormatInvalid: len > 10,
      shouldCheckDuplicate: len === 10,
    };
    const neutral = {
      isChecking: false,
      isDuplicate: false,
      duplicateCustomer: null,
      isVerifiedUnique: false,
    };

    // > 10 digits → invalid. Never call the API, never verify.
    if (base.isFormatInvalid) {
      return { ...base, ...neutral, errorMessage: "Enter a valid phone number", iconType: "error" };
    }

    // Empty or 1–9 digits → neutral. No error, no icon, no API call.
    if (base.isEmpty || base.isIncomplete) {
      return { ...base, ...neutral, errorMessage: undefined, iconType: "none" };
    }

    // Exactly 10 digits. Trust an API result only when it matches the CURRENT
    // live value (`inSync`) — this stops a stale "unique" result from a
    // previous number lingering as a green tick.
    if (inSync && ready && !isFetching) {
      const d = data?.data;
      if (d?.exists && d.customer_id) {
        const duplicateCustomer: DuplicateCustomer = {
          customer_id: d.customer_id,
          customer_name: d.customer_name || "",
        };
        return {
          ...base,
          isChecking: false,
          isDuplicate: true,
          duplicateCustomer,
          isVerifiedUnique: false,
          errorMessage: duplicateCustomer.customer_name
            ? `Phone already belongs to ${duplicateCustomer.customer_name}.`
            : "A customer with this phone number already exists.",
          iconType: "error",
        };
      }
      if (d) {
        // Server confirmed NOT a duplicate.
        return {
          ...base,
          isChecking: false,
          isDuplicate: false,
          duplicateCustomer: null,
          isVerifiedUnique: true,
          errorMessage: undefined,
          iconType: "success",
        };
      }
      if (error) {
        // Lookup failed — don't trap the user on a spinner; the backend's
        // unique index is the final guard at save time.
        return { ...base, ...neutral, errorMessage: undefined, iconType: "none" };
      }
    }

    // Still resolving — debounce pending, request in flight, or just queued.
    return {
      ...base,
      isChecking: true,
      isDuplicate: false,
      duplicateCustomer: null,
      isVerifiedUnique: false,
      errorMessage: undefined,
      iconType: "spinner",
    };
  }, [liveDigits, inSync, ready, isFetching, data, error]);

  // TEMP — validation-state audit logging. Remove after verification.
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.table(phoneState);
  }, [phoneState]);

  return phoneState;
}
